<template>
  <BlokkliProvider v-bind="blokkliProps">
    <div class="pt-20 md:pt-30 lg:pt-40 xl:pt-50 flex flex-col gap-30">
      <!-- Semantically, the date isn't the h1, but the name of the event is. We, therefore, switch them around with CSS. -->
      <Container
        class="flex flex-col w-full"
        :class="{
          'gap-20': isTrulyRepeating || !hasMultipleOccurrences,
          'gap-10': !isTrulyRepeating && hasMultipleOccurrences,
        }"
      >
        <h1 class="text-4xl font-bold order-2">{{ title }}</h1>

        <div v-if="titleDate" class="text-6xl font-bold text-green-600 order-1">
          {{ titleDate }}
        </div>

        <div
          v-if="isTrulyRepeating || !hasMultipleOccurrences"
          class="font-bold flex flex-col order-3 capitalize"
        >
          <span>
            {{ nextOccurrenceDate }}
          </span>
          <span v-if="placeLabels" class="text-green-600">
            {{ placeLabels }}
          </span>
        </div>

        <div
          v-else-if="placeLabels"
          class="text-2xl font-bold text-green-600 pb-10 order-3"
        >
          {{ placeLabels }}
        </div>

        <div
          v-if="hasMultipleOccurrences || isTrulyRepeating"
          class="order-4 flex gap-10"
        >
          <div v-if="hasMultipleOccurrences && !isTrulyRepeating">
            <div class="tag is-dark">
              <SpriteSymbol name="calendar" aria-hidden="true" />
              {{ $texts('event.hasMultiple', 'Mehrere Termine') }}
            </div>
          </div>

          <div v-if="isTrulyRepeating">
            <div class="tag">
              <SpriteSymbol name="calendar" aria-hidden="true" />
              {{ $texts('event.isRecurring', 'Fortlaufender Termin') }}
            </div>
          </div>

          <div v-if="!!signupLink?.uri?.path">
            <div class="tag is-light">
              <SpriteSymbol name="pen" aria-hidden="true" />
              {{ $texts('event.withSignUp', 'Mit Anmeldung') }}
            </div>
          </div>
        </div>
      </Container>
      <Container v-if="lead" class="w-full text-2xl font-bold">
        {{ lead }}
      </Container>
      <Container v-if="image" class="w-full">
        <MediaImage
          v-bind="image"
          image-style="header"
          img-class="w-full"
          loading="eager"
          preload
        />
      </Container>
      <Container v-if="description" class="w-full mb-10">
        <div
          class="ck-content hyphens-auto lg:hyphens-none"
          v-html="description"
        />
      </Container>
      <Container
        v-if="hasMultipleOccurrences && !isTrulyRepeating"
        class="w-full text-lg"
      >
        <h2 class="text-4xl font-bold mb-20">
          {{ $texts('event.occurrences', 'Termine') }}
        </h2>

        <ol>
          <li v-for="(occurrence, i) in occurrences" :key="i">
            <span class="font-bold capitalize"
              >{{ occurrence.start.weekday }}
              {{ occurrence.start.fullDate }}</span
            >,
            {{
              $texts('event.timeOnly', '@time Uhr').replace(
                '@time',
                occurrence.start.time,
              )
            }}
            -
            {{
              $texts('event.timeOnly', '@time Uhr').replace(
                '@time',
                occurrence.end?.time || '',
              )
            }}
          </li>
        </ol>
      </Container>
      <Container v-if="hasButtons" class="w-full mt-10 mb-40 flex gap-8">
        <a
          v-if="!!signupLink?.uri?.path"
          :href="signupLink?.uri?.path"
          class="button is-strong is-link"
          target="_blank"
        >
          {{ $texts('event.register', 'Anmelden') }}
          <span class="sr-only">
            {{
              $texts(
                'accessibility.externalLinkText',
                'Externer Link, wird in einem neuen Tab oder Fenster geöffnet',
              )
            }}
          </span>
        </a>

        <button v-if="!isTrulyRepeating" class="button" @click="saveToCalendar">
          <SpriteSymbol name="calendar" aria-hidden="true" />
          {{ $texts('event.save', 'Speichern') }}
        </button>

        <button v-if="canShare && shareData" class="button" @click="sharePage">
          <SpriteSymbol name="share" aria-hidden="true" />
          {{ $texts('event.share', 'Teilen') }}
        </button>
      </Container>
    </div>

    <BoxSection
      v-if="
        places?.some((p) => p.address) || paragraphsContact || organizations
      "
      two-rows
      class="mt-30"
    >
      <BoxSectionItem
        v-if="places && places.length > 0 && places.some((p) => p.address)"
        :title="
          places.filter((p) => p.address).length > 1
            ? $texts('events.places', 'Veranstaltungsorte')
            : $texts('events.place', 'Veranstaltungsort')
        "
        class="row-span-2"
      >
        <TaxonomyTermPlace
          v-for="place in places.filter((p) => p.address)"
          v-bind="place"
          :key="`place_${place.label}`"
        />
      </BoxSectionItem>

      <BoxSectionItemParagraphs
        :title="$texts('events.contact', 'Kontakt')"
        :list="paragraphsContact"
      />

      <BoxSectionItemOrganizations
        :organizations="organizations"
        title="department"
      />
      <BoxSectionItemUpdate :date="lastUpdate?.first" />
    </BoxSection>
  </BlokkliProvider>
</template>

<script lang="ts" setup>
import type {
  NodeEventFragment,
  OccurrenceFieldsFragment,
} from '#graphql-operations'
import downloadIcsFile from '~/helpers/downloadIcsFile'
import { slugify } from '~/helpers/slugify'
import { type WithRequired, withRequiredProperties } from '~/helpers/type'

const { $texts } = useNuxtApp()

const props = defineProps<{
  title?: string
  uuid: string
  lastUpdate?: NodeEventFragment['lastUpdate']
  dateRecur?: NodeEventFragment['dateRecur']
  occurrences?: NodeEventFragment['occurrences']
  places?: NodeEventFragment['places']
  signupLink?: NodeEventFragment['signupLink'] | null
  lead?: NodeEventFragment['lead']
  image?: NodeEventFragment['image']
  description?: NodeEventFragment['description']
  teaserText?: NodeEventFragment['teaserText']
  organizations?: NodeEventFragment['organizations']
  paragraphsContact?: NodeEventFragment['paragraphsContact']
  blokkliProps: NodeEventFragment['blokkliProps']
}>()

// Convenience computed for simpler access
const occurrences = computed<WithRequired<OccurrenceFieldsFragment, 'start'>[]>(
  () => {
    return props.occurrences?.occurrences
      ? withRequiredProperties(props.occurrences.occurrences, ['start'])
      : []
  },
)

// "Truly" because repeating events automatically also have multiple occurences.
const isTrulyRepeating = computed(() => {
  return !!props.dateRecur?.some((dateRecurs) => {
    return dateRecurs.rrule !== ''
  })
})

const hasMultipleOccurrences = computed(
  () => (occurrences.value?.length || 0) > 1,
)

const titleDate = computed(() => {
  if (hasMultipleOccurrences.value) {
    return null
  }

  if (props.dateRecur) {
    return props.dateRecur[0].mediumDate
  }
  return ''
})

const placeLabels = computed(() => {
  return props.places?.map((p) => p.label).join(', ')
})

const nextOccurrenceDate = computed(() => {
  if (!occurrences.value) {
    return ''
  }

  // Get first occurrence
  const firstOccurrence: OccurrenceFieldsFragment = occurrences.value[0]
  if (!firstOccurrence) {
    return
  }

  // Get formatted start and end values.
  const startFormatted = parseInt(firstOccurrence.start?.formatted || '0')
  const endFormatted = parseInt(firstOccurrence.end?.formatted || '0')

  // Calculate duration.
  const durationHours = Math.round((endFormatted - startFormatted) / 60 / 60)

  // If the duration can be divided by 24 without any remainders, then display
  // the entire day.
  if (durationHours % 24 === 0) {
    return $texts('event.entireDay', 'Ganztags')
  }

  // Format state time.
  const startTimeFormatted =
    firstOccurrence.start?.weekday + ' ' + firstOccurrence.start?.time

  // Format end time.
  let endTimeFormatted: string = ''
  if (firstOccurrence.start?.weekday !== firstOccurrence.end?.weekday) {
    endTimeFormatted = firstOccurrence.end?.weekday + ' '
  }
  endTimeFormatted = endTimeFormatted + firstOccurrence.end?.time || ''

  return $texts('event.startAndEndTime', '@startTime - @endTime Uhr')
    .replace('@startTime', startTimeFormatted)
    .replace('@endTime', endTimeFormatted)
})

const nextOccurrenceRRule = computed(() => {
  if (props.dateRecur) {
    return props.dateRecur[0].rrule
  }

  return undefined
})

const canShare = ref(false)
const shareData = ref<{
  title: string
  text: string
  url: string
}>()

const hasButtons = computed(() => {
  return (
    canShare.value || !!props.signupLink?.uri?.path || isTrulyRepeating.value
  )
})

onMounted(() => {
  if (!navigator.share) {
    return
  }

  canShare.value = true

  shareData.value = {
    title: props.title || '',
    text: props.teaserText || '',
    url: window.location.href,
  }
})

async function sharePage() {
  try {
    await navigator.share(shareData.value)
  } catch (e) {
    // Do nothing.
  }
}

const inlinedAddress = computed(() => {
  if (!props.places?.some((p) => p.address)) {
    return ''
  }

  return props.places
    .map((p) => {
      if (!p.address) {
        return ''
      }

      const addressParts = [
        p.address.organization,
        p.address.addressLine1,
        p.address.addressLine2,
      ]

      if (p.address.postalCode && p.address.locality) {
        addressParts.push(p.address.postalCode + ' ' + p.address.locality)
      }
      return addressParts.filter((el) => el && el.length > 0).join(', ')
    })
    .join(', ')
})

const plainTextTeaserText = computed(() => {
  const tempDivForDescription = document.createElement('div')
  tempDivForDescription.innerHTML = props.teaserText as string

  return tempDivForDescription.textContent || tempDivForDescription.innerText
})

const mainOrganizer = computed(() => {
  if (!props.organizations || props.organizations.length === 0) {
    return null
  }

  return props.organizations[0]
})

function saveToCalendar() {
  if (!occurrences.value) {
    return ''
  }

  // We either want a rrule _or_ multiple occurrences, never both.
  let occurences = occurrences.value
  let rrule

  if (isTrulyRepeating.value) {
    occurences = [occurences[0]]
    rrule = nextOccurrenceRRule.value
  }

  downloadIcsFile(
    slugify(props.title as string),
    props.uuid,
    props.title as string,
    plainTextTeaserText.value,
    occurences,
    window.location.href,
    inlinedAddress.value,
    mainOrganizer.value?.name,
    mainOrganizer.value?.email,
    rrule,
  )
}
</script>
